import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, Spinner } from '@indeed/ifl-components';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import { useCurrentFlow } from '../../lib/pageFlow/useCurrentFlow';
import { useInitialState } from '../../lib/context/InitialStateContext';
import GettextFactory from '../../util/GettextFactory';
import { useGetAutoApplySetupEntryModeQuery } from './__generated__/AutoApplySetupIndexPage.generated';
import { AutoApplySetupRumContext, logAutoApplySetupRumError } from './autoApplySetupRumErrors';

const REVIEW_BEFORE_SUBMIT_PATH = '/auto-apply-setup/review/review-before-submit';

/**
 * Auto Apply Setup Index Page
 * Redirects to the first step of the flow as determined by useAutoApplySetupFlow.
 * The Apply For Me disabled treatment overrides that entry route with Review Before Submit.
 *
 * Uses Navigate component which works on both server-side and client-side rendering,
 * avoiding unnecessary useEffect client-side redirects.
 */
const AutoApplySetupIndexPage: React.FC = () => {
    const location = useLocation();
    const { allSteps } = useCurrentFlow();
    const { entryPoint } = useInitialState();
    const { getGroup } = useProctorGroups();
    const isApplyForMeDisabled = getGroup('apply_for_me_sfm_tog')?.value === 0;
    const gt = GettextFactory();
    const isProgressiveAutomation = getGroup('rnhp_auto_apply_banner_tst')?.value === 2;

    const firstStepRoute = allSteps[0]?.routes[0] ?? '/auto-apply-setup/resume';
    const opensOnReview =
        isProgressiveAutomation && firstStepRoute.startsWith('/auto-apply-setup/review');

    // Entry-driven review variant (IAFE-2081). When Apply For Me is available and the
    // progressive-automation flow opens on Review, the variant matches where the user
    // came from so the disclaimer/consent and button behavior line up:
    //   - homepage entries → review-before-submit
    //   - known non-homepage entries (MyJobs/promo) → apply-for-me
    //   - unknown entries → no signal from the origin, so default to the
    //     conservative review-before-submit, unless the user is an already-activated
    //     Apply For Me user (ENABLED + APPLY_FOR_ME per IAFE-2607),
    //     in which case keep them on apply-for-me.
    const isHomepageEntry =
        !!entryPoint && ['HOMEPAGE', 'JOB_SEARCH', 'homepage', 'RnHomepage'].includes(entryPoint);
    const isUnknownEntry = !entryPoint || entryPoint === 'UNKNOWN';

    // Only the unknown-entry case needs the user's current mode. The disabled treatment
    // skips this lookup because every entry resolves synchronously to Review Before Submit.
    const needsModeLookup = !isApplyForMeDisabled && opensOnReview && isUnknownEntry;
    const { data, loading, error } = useGetAutoApplySetupEntryModeQuery({
        skip: !needsModeLookup,
        fetchPolicy: 'cache-first',
        errorPolicy: 'all',
    });

    React.useEffect(() => {
        if (error) {
            logAutoApplySetupRumError(error, AutoApplySetupRumContext.EntryModeLookup, false);
        }
    }, [error]);

    // Hold the redirect only while resolving the unknown-entry mode. On failure
    // (errorPolicy 'all'), data is absent and we fall through to the safe default.
    if (needsModeLookup && loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 8,
                    minBlockSize: '200px',
                }}
            >
                <Spinner title={gt.gettext('Loading...')} />
            </Box>
        );
    }

    let reviewVariant: 'review-before-submit' | 'apply-for-me' = 'review-before-submit';
    if (isHomepageEntry) {
        reviewVariant = 'review-before-submit';
    } else if (!isUnknownEntry) {
        reviewVariant = 'apply-for-me';
    } else if (
        data?.autoApplyInfo?.autoApplyStatus === 'ENABLED' &&
        data?.autoApplyInfo?.mode === 'APPLY_FOR_ME'
    ) {
        reviewVariant = 'apply-for-me';
    }

    const target = isApplyForMeDisabled
        ? REVIEW_BEFORE_SUBMIT_PATH
        : opensOnReview
          ? `/auto-apply-setup/review/${reviewVariant}`
          : firstStepRoute;

    // Navigate handles the redirect on both SSR and client; append the current search
    // string explicitly so entry and force-group parameters survive.
    return <Navigate to={`${target}${location.search}`} replace />;
};

export default AutoApplySetupIndexPage;
